import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { commandRouter } from '../utils/CommandRouter.js';
import { sessionSummaryService } from '../api/sessionSummary.js';
import { arcWebhookHandler } from '../api/arcWebhookHandler.js';
import { arcTelemetryService } from '../api/arcTelemetry.js';
import { useSettingsStore } from './useSettingsStore.js';
import { chatAPI, type ChatSegment, restChatAPI, mapBackendHistoryToUi } from '../api/chatApi.js';
import { closeContextCanvas } from '../studio/canvas/index.js';
import type {
    ChatMessage as BaseChatMessage,
    ChatMessageSender,
    ChatMessageType as BaseChatMessageType,
} from '../../../src/shared/chat/chatTypes.js';

const PREFER_REST_CHAT = true;
const ENABLE_SOMA_RUN_LOG =
    ((import.meta as any).env?.['VITE_ENABLE_SOMA_RUN_LOG'] as string | undefined) !== 'false';

let notifyFunction: ((title: string, options?: Record<string, unknown>) => void) | null = null;

export const setNotifyFunction = (
    notify: (title: string, options?: Record<string, unknown>) => void
) => {
    notifyFunction = notify;
};

export type MessageType = 'chat' | 'feedback';

export interface MetaBuildStage {
    name: string;
    status: string;
}

export interface MetaBuildResult {
    success: boolean;
    dryRun?: boolean;
    specPath?: string;
    targetName?: string;
    prUrl?: string | null;
    branchName?: string | null;
    qualityGatePassed?: boolean | null;
    stages?: MetaBuildStage[];
    // Allow backend to add fields without breaking UI typing
    [key: string]: unknown;
}

export interface StudioRepoContext {
    repo?: string | null;
    branch?: string | null;
    commitSha?: string | null;
}

export interface PlanPreviewData {
    status: 'pending' | 'approved' | 'cancelled';
    prompt: string;
    traceId?: string;
    intentClass?: string;
    intentName?: string;
    runMode?: boolean;
    steps: string[];
    context?: AgenticContext | null;
    studioContext?: StudioRepoContext | null;
    createdAt: string;
}

export interface BeeLogChunk {
    phase: 'start' | 'action' | 'plan' | 'suggestion' | 'done';
    title?: string;
    timestamp?: string;
    entries?: string[];
    content?: string;
}

export interface ChatMessage {
    id: string;
    sender: 'user' | 'bee' | 'system' | 'assistant';
    content: string;
    type:
        | 'text'
        | 'code'
        | 'pluginCard'
        | 'taskResult'
        | 'assistant-log'
        | 'soma-response'
        | 'run-log'
        | 'plan-preview'
        | 'intelligent-response';
    messageType?: MessageType;
    timestamp: string;
    buildCredits?: number;
    relatedTaskId?: string;
    log?: BeeLogChunk[];
    sessionId?: string;
    metadata?: {
        model?: string;
        confidence?: number;
        explanation?: string;
        threadId?: string;
        traceId?: string;
        streamToken?: string;
        artifactRedirect?: boolean;
        artifactId?: string;
        artifactTitle?: string;
        artifactMime?: string;
        artifactSizeBytes?: number;
        reason?: string;
        risk?: string;
        /** Stream URL for RunTranscriptMessage SSE connection */
        streamUrl?: string;
        /** Intent class from classification */
        intentClass?: string;
        /** Intent name from classification */
        intentName?: string;
        /** Whether this is a run mode request */
        runMode?: boolean;
        /** Structured MetaBuild result payload (if applicable) */
        metaBuild?: MetaBuildResult | null;
        /** Context used for decision/execution (chat-side, not backend-derived) */
        context?: AgenticContext | null;
        /** Repo/branch context (best-effort from /api/studio/context) */
        studioContext?: StudioRepoContext | null;
        /** Plan preview payload (for approve/execute gating) */
        planPreview?: PlanPreviewData | null;
        /**
         * Execution envelope from SOMA spine (constitutional compliance).
         * Contains verdict, artifacts, and reasonCode for proper UI rendering.
         */
        executionEnvelope?: {
            mode?: 'answer_only' | 'execution_required';
            executed?: boolean;
            /** Step 7: plan-only mode flag (mode remains execution_required, executed=false) */
            planOnly?: boolean;
            jobId?: string | null;
            executionId?: string | null;
            verdict?: 'SUCCESS' | 'FAILED' | 'INCOMPLETE' | 'BLOCKED' | 'DENIED' | 'NOT_APPLICABLE';
            artifactPointers?:
                | string[]
                | {
                      plan?: string;
                      trace?: string;
                      verification?: string;
                      ledger?: string;
                  };
            artifacts?: {
                plan?: string;
                trace?: string;
                verification?: string;
                ledger?: string;
            };
            reasonCode?: string;
            intentName?: string;
            intentClass?: string;
            nextActions?: Array<{
                label: string;
                type: 'button' | 'link' | 'suggestion';
                payload: string;
            }>;
            verified?: boolean;
        } | null;
        /** @deprecated Use executionEnvelope.verdict instead. Legacy verified flag */
        verified?: boolean;
        /**
         * CQC-F-022: Full content before truncation.
         * When presentation enforcement truncates content, the full original
         * content is preserved here for copy operations. Copy handlers must
         * use this as the canonical source for clipboard operations.
         */
        contentRaw?: string;
    };
}

export type BeeControlMode = 'manual' | 'semi' | 'full';

export interface TaskMetadata {
    title: string;
    createdAt: Date;
    sessionId: string;
    taskId?: string;
}

export interface RetrievalResult {
    text: string;
    score?: number;
    metadata?: Record<string, unknown>;
}

interface Session {
    id: string;
    name: string;
    createdAt?: string;
}

export interface AgenticContext {
    activeFile?: string;
    activePanel?: string;
    selectedAgent?: string;
    currentWorkflow?: string;
}

export interface ChatStore {
    messages: ChatMessage[];
    isLoading: boolean;
    sessionId: string | null;
    sessionTitle: string | null;
    sessions: Session[];
    taskMetadata: TaskMetadata | null;
    controlMode: BeeControlMode;
    beeControlMode: BeeControlMode;
    editorContent: Record<string, string>;
    openFiles: Record<string, string[]>;
    activeFile: Record<string, string>;
    prCount: number;
    ciCount: number;
    buildCredits: number;
    currentContext: AgenticContext | null;
    missionId: string | null;
    hydratedSessions: Set<string>;
    /** Phase C: Cursor for loading older messages */
    messagesCursor: string | null;
    /** Phase C: Whether there are more older messages to load */
    hasMoreMessages: boolean;
    /** Phase C: Loading state for older messages */
    isLoadingOlder: boolean;
    /** Phase D: Whether the session has been auto-named (prevents re-naming) */
    sessionAutoNamed: boolean;
    /** Phase D: Timestamp of last failed auto-name attempt */
    autoNameAttemptedAt: number | null;
    /** Phase D: Number of auto-name attempts (0=never, 1=failed once, 2=exhausted) */
    autoNameAttemptCount: number;
    /** Phase D: User message count when auto-name failed (for retry on next message) */
    autoNameFailedUserMsgCount: number | null;
    /** Phase D: Whether an auto-name PATCH is in flight */
    autoNameInFlight: boolean;
    setSessionId: (id: string) => void;
    setSessionTitle: (title: string) => void;
    setMissionId: (missionId: string | null) => void;
    hydrateFromBackend: (sessionId: string) => Promise<void>;
    sendMessage: (content: string) => Promise<void>;
    addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
    addChat: (msg: { author: 'user' | 'assistant'; text: string }) => void;
    addFeedback: (text: string) => void;
    clearMessages: () => void;
    injectWebhookResult: (message: ChatMessage) => void;
    executeCommand: (command: string) => Promise<void>;
    executeAgenticCommand: (command: string) => Promise<void>;
    setContext: (context: AgenticContext) => void;
    getMessagesByTaskId: (taskId: string) => ChatMessage[];
    switchControlMode: (mode: BeeControlMode) => void;
    switchMode: (mode: BeeControlMode) => void;
    setControlMode: (mode: BeeControlMode) => void;
    setLoading: (loading: boolean) => void;
    simulateWebhookEvents: () => Promise<void>;
    simulateCIEvents: () => Promise<void>;
    initializeWebhookListeners: () => void;
    updateTaskTitle: (title: string) => void;
    generateTaskTitle: (firstMessage: string) => Promise<void>;
    generateSessionTitle: (firstMessage: string) => Promise<void>;
    setEditorContent: (content: string, fileName?: string) => void;
    getEditorContent: (fileName?: string) => string;
    setOpenFiles: (files: string[]) => void;
    getOpenFiles: () => string[];
    setActiveFile: (fileName: string) => void;
    getActiveFile: () => string;
    saveCurrentSession: () => void;
    loadSession: (sessionId: string) => Promise<void>;
    setSession: (id: string) => void;
    renameSession: (id: string, newName: string) => void;
    createSession: ({
        id,
        title,
        plugins,
        messages,
    }: {
        id: string;
        title: string;
        plugins: string[];
        messages?: Array<{ role: string; text: string }>;
    }) => void;
    createNewSession: () => string;
    searchTasks: (query: string) => Session[];
    addLogChunks: (log: BeeLogChunk[]) => void;
    /** Phase C: Clear session state for blank load or new session */
    clearSession: () => void;
    /** Phase C: Load session with pagination (50 messages) */
    loadSessionWithPagination: (sessionId: string) => Promise<void>;
    /** Phase C: Load older messages using cursor */
    loadOlderMessages: () => Promise<void>;
    /** Phase D: Auto-name session after 2-4 user messages (name freezes after first naming) */
    maybeAutoNameSession: () => Promise<void>;
    /** Update existing run-log message by traceId instead of appending duplicate */
    updateMessageByTraceId: (
        traceId: string,
        updates: {
            status?: string;
            answer?: string;
            steps?: unknown[];
            failureReason?: string;
            metaBuild?: MetaBuildResult | null;
        }
    ) => boolean;
    /** Approve and execute a pending plan-preview card */
    approvePlanPreview: (messageId: string) => Promise<void>;
    /** Cancel a pending plan-preview card */
    cancelPlanPreview: (messageId: string) => void;
}

const generateMessageId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const generateSessionId = () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const useChatStore = create<ChatStore>()(
    persist(
        (set, get): ChatStore => {
            const getStudioRepoContext = async (): Promise<StudioRepoContext | null> => {
                try {
                    const res = await fetch('/api/studio/context', { credentials: 'include' });
                    if (!res.ok) return null;
                    const data = await res.json().catch(() => ({}));
                    const ctx = (data as any)?.context;
                    if (!ctx || typeof ctx !== 'object') return null;
                    return {
                        repo: typeof ctx.repo === 'string' ? ctx.repo : (ctx.repo ?? null),
                        branch: typeof ctx.branch === 'string' ? ctx.branch : (ctx.branch ?? null),
                        commitSha:
                            typeof ctx.commitSha === 'string'
                                ? ctx.commitSha
                                : (ctx.commitSha ?? null),
                    };
                } catch {
                    return null;
                }
            };

            const isCodeAffectingIntent = (
                intentClass?: string,
                intentName?: string,
                messageContent?: string
            ): boolean => {
                const cls = (intentClass || '').toLowerCase();
                const name = (intentName || '').toLowerCase();
                const msg = (messageContent || '').toLowerCase();

                // Client-side metabuild gate: check message content for metabuild apply/openPR
                // This ensures Plan Preview gate triggers without backend dependency
                const isMetaBuildApply =
                    (msg.includes('metabuild') ||
                        msg.includes('meta-build') ||
                        msg.includes('meta build')) &&
                    (msg.includes('open pr') || msg.includes('openpr') || msg.includes('apply')) &&
                    !msg.includes('dry run') &&
                    !msg.includes('dryrun');

                if (isMetaBuildApply) {
                    return true;
                }

                // Minimal heuristic: only gate obvious code-affecting intents.
                // This intentionally avoids gating "metrics/report" style intents used in tests.
                return (
                    cls === 'dev' &&
                    (name.includes('meta_build') ||
                        name.includes('metabuild') ||
                        name.includes('build') ||
                        name.includes('deploy') ||
                        name.includes('patch') ||
                        name.includes('fix') ||
                        name.includes('pr'))
                );
            };

            const buildPlanSteps = (intentName?: string): string[] => {
                const name = (intentName || '').toLowerCase();
                if (name.includes('meta_build') || name.includes('metabuild')) {
                    return ['Validate spec', 'Generate code', 'Run quality gate', 'Open PR'];
                }
                if (name.includes('deploy')) {
                    return ['Validate environment', 'Build', 'Deploy', 'Verify health'];
                }
                if (name.includes('fix') || name.includes('patch')) {
                    return ['Inspect repo context', 'Apply patch', 'Run tests/quality', 'Open PR'];
                }
                return ['Inspect repo context', 'Plan changes', 'Execute', 'Verify'];
            };

            const updateMessageById = (
                messageId: string,
                updater: (m: ChatMessage) => ChatMessage
            ): boolean => {
                const messages = get().messages;
                const idx = messages.findIndex(m => m.id === messageId);
                if (idx === -1) return false;
                const updated = updater(messages[idx]);
                set({
                    messages: [...messages.slice(0, idx), updated, ...messages.slice(idx + 1)],
                });
                return true;
            };

            const executeTracedSomaRun = async (opts: {
                content: string;
                intentClass?: string;
                intentName?: string;
                runMode?: boolean;
                decisionTraceId?: string;
            }): Promise<void> => {
                const base = (import.meta as any).env?.['VITE_BACKEND_URL'] || '';
                const studioContext = await getStudioRepoContext();

                // Use let so we can reassign to traceId from /run/init
                let runTraceId = opts.decisionTraceId || `trace_${Date.now()}`;

                try {
                    // Step 1: Initialize the run and get stream credentials
                    const initRes = await fetch(`${base}/api/soma/run/init`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...((import.meta as any).env?.['VITE_HIVECORE_JWT']
                                ? {
                                      Authorization: `Bearer ${(import.meta as any).env.VITE_HIVECORE_JWT}`,
                                  }
                                : {}),
                            ...(import.meta.env['VITE_HIVECORE_INTERNAL_TOKEN']
                                ? {
                                      'X-HiveCore-Internal-Token': import.meta.env[
                                          'VITE_HIVECORE_INTERNAL_TOKEN'
                                      ],
                                  }
                                : {}),
                        },
                        credentials: 'include',
                    });

                    const initData = await initRes.json().catch(() => ({}));
                    if (!initRes.ok || !initData?.data?.traceId) {
                        const initError = initData?.error || `HTTP ${initRes.status}`;
                        console.error('[ChatStore] Run init failed:', initError);
                        get().addMessage({
                            sender: 'bee',
                            content: `Run initialization failed: ${initError}. Unable to start traced run.`,
                            type: 'text',
                            messageType: 'chat',
                            buildCredits: 5,
                        });
                        set({ isLoading: false });
                        return;
                    }

                    runTraceId = initData.data['traceId'];
                    const streamToken = initData.data.streamToken;

                    // Step 2: Insert a RunLog bubble with stream token for live updates
                    get().addMessage({
                        sender: 'bee',
                        content: JSON.stringify({
                            traceId: runTraceId,
                            status: 'RUNNING',
                            mode: 'SOMA_DIRECT',
                            startedAt: new Date().toISOString(),
                            steps: [],
                            streamToken,
                        }),
                        type: 'run-log',
                        messageType: 'chat',
                        buildCredits: 0,
                        metadata: {
                            traceId: runTraceId,
                            streamToken,
                            intentClass: opts.intentClass,
                            intentName: opts.intentName,
                            runMode: opts.runMode,
                            context: get().currentContext,
                            studioContext,
                        },
                    });

                    // Step 3: Call /api/soma/chat with the traceId
                    const response = await fetch(`${base}/api/soma/chat`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...((import.meta as any).env?.['VITE_HIVECORE_JWT']
                                ? {
                                      Authorization: `Bearer ${(import.meta as any).env.VITE_HIVECORE_JWT}`,
                                  }
                                : {}),
                            ...(import.meta.env['VITE_HIVECORE_INTERNAL_TOKEN']
                                ? {
                                      'X-HiveCore-Internal-Token': import.meta.env[
                                          'VITE_HIVECORE_INTERNAL_TOKEN'
                                      ],
                                  }
                                : {}),
                            'X-Trace-Id': runTraceId,
                            'X-Agent-Profile': 'soma_dev_readonly',
                        },
                        body: JSON.stringify({
                            message: opts.content,
                            sessionId: get().sessionId || `beehive-${Date.now()}`,
                            traceId: runTraceId,
                            context: get().currentContext || undefined,
                            reply_mode: 'dialogue',
                            tenant_id: 'desktop-user',
                            app_id: 'hivecore',
                            user_id: 'anonymous',
                            data_tier: 'INTERNAL',
                            rbac_claims: {
                                roles: ['viewer'],
                                namespaces: ['hivecore://**'],
                                tiers: ['PUBLIC', 'INTERNAL'],
                                capabilities: ['can_read_kb'],
                            },
                        }),
                        credentials: 'include',
                    });

                    const data = await response.json().catch(() => ({}));

                    if (!response.ok) {
                        const required = Array.isArray(data?.required_actions)
                            ? data.required_actions
                            : [];
                        const isAuth = response.status === 401 || response.status === 403;
                        const isServer = response.status >= 500;
                        const rawStatus = data?.status || `http_${response.status}`;
                        const status = isAuth
                            ? `unauthorized (${rawStatus})`
                            : isServer
                              ? `unavailable (${rawStatus})`
                              : `unavailable (${rawStatus})`;

                        const msg =
                            required.length > 0
                                ? `Blocked (${status}).\n\nRequired actions:\n- ${required.join('\n- ')}`
                                : `Blocked (${status}).`;
                        get().addMessage({
                            sender: 'bee',
                            content: msg,
                            type: 'text',
                            messageType: 'chat',
                            buildCredits: Math.ceil(msg.length / 4),
                        });
                        set({ isLoading: false });
                        return;
                    }

                    // Extract response and transcript from the data
                    const responseData = (data as any)?.data || data;
                    const responseText =
                        (responseData &&
                            typeof responseData.response === 'string' &&
                            responseData.response) ||
                        (responseData &&
                            typeof responseData.message === 'string' &&
                            responseData.message) ||
                        '';
                    const responseEnvelope = responseData?.executionEnvelope as
                        | {
                              verdict?: string;
                              reasonCode?: string;
                          }
                        | undefined;
                    const responseConfidence =
                        typeof responseData?.confidence === 'number'
                            ? (responseData.confidence as number)
                            : undefined;
                    const transcript = responseData?.transcript;
                    const metaBuild: MetaBuildResult | null =
                        responseData &&
                        typeof responseData.metaBuild === 'object' &&
                        responseData.metaBuild !== null
                            ? (responseData.metaBuild as MetaBuildResult)
                            : null;

                    // Fail-closed: If no response and no transcript steps, show INVALID SUCCESS
                    if (!responseText && (!transcript || transcript.steps?.length <= 1)) {
                        const updated = get().updateMessageByTraceId(runTraceId, {
                            status: 'INVALID_SUCCESS',
                            steps: transcript?.steps || [],
                            failureReason: 'Run completed without emitting steps or output.',
                            verdict: responseEnvelope?.verdict,
                            reasonCode: responseEnvelope?.reasonCode,
                            confidence: responseConfidence,
                            metaBuild,
                        });
                        if (!updated) {
                            get().addMessage({
                                sender: 'bee',
                                content: JSON.stringify({
                                    traceId: runTraceId,
                                    status: 'INVALID_SUCCESS',
                                    mode: 'SOMA_DIRECT',
                                    startedAt: new Date().toISOString(),
                                    steps: transcript?.steps || [],
                                    failureReason:
                                        'Run completed without emitting steps or output.',
                                    verdict: responseEnvelope?.verdict,
                                    reasonCode: responseEnvelope?.reasonCode,
                                    confidence: responseConfidence,
                                    metaBuild,
                                }),
                                type: 'run-log',
                                messageType: 'chat',
                                buildCredits: 0,
                                metadata: {
                                    traceId: runTraceId,
                                    status: 'INVALID_SUCCESS',
                                    metaBuild,
                                },
                            });
                        }
                    } else if (transcript) {
                        const updated = get().updateMessageByTraceId(runTraceId, {
                            status: transcript.status || 'SUCCESS',
                            steps: transcript.steps || [],
                            answer: responseText,
                            verdict: responseEnvelope?.verdict,
                            reasonCode: responseEnvelope?.reasonCode,
                            confidence: responseConfidence,
                            metaBuild,
                        });
                        if (!updated) {
                            get().addMessage({
                                sender: 'bee',
                                content: JSON.stringify({
                                    traceId: runTraceId,
                                    status: transcript.status || 'SUCCESS',
                                    mode: 'SOMA_DIRECT',
                                    startedAt: new Date().toISOString(),
                                    steps: transcript.steps || [],
                                    answer: responseText,
                                    verdict: responseEnvelope?.verdict,
                                    reasonCode: responseEnvelope?.reasonCode,
                                    confidence: responseConfidence,
                                    metaBuild,
                                }),
                                type: 'run-log',
                                messageType: 'chat',
                                buildCredits: 0,
                                metadata: {
                                    traceId: runTraceId,
                                    status: transcript.status || 'SUCCESS',
                                    metaBuild,
                                },
                            });
                        }
                    } else if (responseText) {
                        const updated = get().updateMessageByTraceId(runTraceId, {
                            status: 'SUCCESS',
                            answer: responseText,
                            verdict: responseEnvelope?.verdict,
                            reasonCode: responseEnvelope?.reasonCode,
                            confidence: responseConfidence,
                            metaBuild,
                        });
                        if (!updated) {
                            console.warn(
                                '[ChatStore] No run-log found for traceId:',
                                runTraceId,
                                '- creating one'
                            );
                            get().addMessage({
                                sender: 'bee',
                                content: JSON.stringify({
                                    traceId: runTraceId,
                                    status: 'SUCCESS',
                                    mode: 'SOMA_DIRECT',
                                    startedAt: new Date().toISOString(),
                                    steps: [],
                                    answer: responseText,
                                    verdict: responseEnvelope?.verdict,
                                    reasonCode: responseEnvelope?.reasonCode,
                                    confidence: responseConfidence,
                                    metaBuild,
                                }),
                                type: 'run-log',
                                messageType: 'chat',
                                buildCredits: 0,
                                metadata: { traceId: runTraceId, status: 'SUCCESS', metaBuild },
                            });
                        }
                    }

                    // Ensure run-log metadata carries metaBuild too (for rendering)
                    if (metaBuild) {
                        updateMessageById(
                            get().messages.find(
                                m => m.type === 'run-log' && m.metadata?.traceId === runTraceId
                            )?.id || '',
                            m => ({
                                ...m,
                                metadata: {
                                    ...m.metadata,
                                    metaBuild,
                                    intentClass: opts.intentClass,
                                    intentName: opts.intentName,
                                    runMode: opts.runMode,
                                    context: get().currentContext,
                                    studioContext,
                                },
                            })
                        );
                    }

                    set({ isLoading: false });
                    return;
                } catch (err) {
                    arcTelemetryService.logTelemetryEvent({
                        id: `soma_runlog_${Date.now()}`,
                        type: 'error',
                        source: 'soma-run-log',
                        data: {
                            status: 'network_error',
                            error: err instanceof Error ? err.message : String(err),
                            traceId: runTraceId,
                        },
                        timestamp: new Date(),
                    });

                    get().addMessage({
                        sender: 'bee',
                        content: 'Run log unavailable (network_error). Please try again.',
                        type: 'text',
                        messageType: 'chat',
                        buildCredits: 0,
                    });
                    set({ isLoading: false });
                    return;
                }
            };

            return {
                sessionId: null,
                sessionTitle: null,
                sessions: [
                    {
                        id: generateSessionId(),
                        name: 'New Session',
                        createdAt: new Date().toISOString(),
                    },
                ],
                messages: [],
                taskMetadata: null,
                controlMode: 'manual',
                beeControlMode: 'manual',
                editorContent: {},
                openFiles: {},
                activeFile: {},
                isLoading: false,
                prCount: 0,
                ciCount: 0,
                buildCredits: 0,
                currentContext: null,
                missionId: null,
                hydratedSessions: new Set<string>(),
                // Phase C: Message pagination state
                messagesCursor: null,
                hasMoreMessages: false,
                isLoadingOlder: false,
                // Phase D: Auto-naming state
                sessionAutoNamed: false,
                autoNameAttemptedAt: null,
                autoNameAttemptCount: 0,
                autoNameFailedUserMsgCount: null,
                autoNameInFlight: false,

                sendMessage: async (content: string) => {
                    set({ isLoading: true });

                    if (!get().taskMetadata && !content.startsWith('/')) {
                        await get().generateTaskTitle(content);
                    }

                    if (content.startsWith('/')) {
                        const userMessage: ChatMessage = {
                            id: generateMessageId(),
                            sender: 'user',
                            content,
                            type: 'text',
                            timestamp: new Date().toISOString(),
                            buildCredits: Math.ceil(content.length / 4),
                        };
                        get().addMessage(userMessage);

                        const result = await commandRouter.execute(content);
                        if (result) {
                            if (result.data?.['triggerSummary']) {
                                const summaryMessage = await sessionSummaryService.endSession(
                                    get().sessionId || 'unknown',
                                    get().messages
                                );
                                get().addMessage(summaryMessage);
                            } else {
                                const messageType =
                                    (result.data?.['type'] as ChatMessage['type']) || 'text';
                                const messageData: Omit<ChatMessage, 'id' | 'timestamp'> = {
                                    sender: messageType === 'soma-response' ? 'bee' : 'system',
                                    content: result.message,
                                    type: messageType as ChatMessage['type'],
                                    buildCredits: Math.ceil(result.message.length / 4),
                                };

                                if (result.data?.['metadata']) {
                                    messageData.metadata = result.data['metadata'];
                                }

                                if (
                                    result.data &&
                                    (result.data as Record<string, unknown>)['taskId']
                                ) {
                                    messageData.relatedTaskId = (
                                        result.data as Record<string, unknown>
                                    )['taskId'] as string;
                                }

                                get().addMessage(messageData);
                            }
                        }
                        set({ isLoading: false });
                        return;
                    }

                    // Track whether we already added a user message (to avoid duplicates)
                    let userMessageAdded = false;

                    if (ENABLE_SOMA_RUN_LOG) {
                        try {
                            const base = (import.meta as any).env?.['VITE_BACKEND_URL'] || '';
                            const classifyRes = await fetch(`${base}/api/soma/classify`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${
                                        (import.meta as any).env?.['VITE_HIVECORE_JWT'] ||
                                        'DEV_JWT_FOR_TESTING'
                                    }`,
                                    ...(import.meta.env['VITE_HIVECORE_INTERNAL_TOKEN']
                                        ? {
                                              'X-HiveCore-Internal-Token': import.meta.env[
                                                  'VITE_HIVECORE_INTERNAL_TOKEN'
                                              ],
                                          }
                                        : {}),
                                },
                                body: JSON.stringify({ message: content }),
                                credentials: 'include',
                            });

                            const classify = await classifyRes.json().catch(() => ({}));

                            // Always show classification decision for visibility into system decision making
                            if (classifyRes.ok && classify?.success) {
                                const traceId = classify.traceId
                                    ? String(classify.traceId)
                                    : undefined;
                                const intentClass = classify.intentClass || 'unknown';
                                const intentName = classify.intentName || 'general';
                                const runMode = classify.runMode === true;
                                const decisionReason =
                                    classify.decision?.reason ||
                                    (runMode
                                        ? `Classified as ${intentClass} intent`
                                        : 'Classified as general chat');

                                // Add user message first
                                const userMessage: ChatMessage = {
                                    id: generateMessageId(),
                                    sender: 'user',
                                    content,
                                    type: 'text',
                                    timestamp: new Date().toISOString(),
                                    buildCredits: Math.ceil(content.length / 4),
                                };
                                get().addMessage(userMessage);
                                userMessageAdded = true;

                                // Always show decision visibility message
                                get().addMessage({
                                    sender: 'system',
                                    content: `Decision: ${intentClass}${intentName ? ` (${intentName})` : ''} | Run Mode: ${runMode ? 'Yes' : 'No'}${traceId ? ` | Trace: ${traceId.slice(0, 8)}...` : ''}\n${decisionReason}`,
                                    type: 'assistant-log',
                                    messageType: 'chat',
                                    buildCredits: 0,
                                    metadata: { traceId, intentClass, intentName, runMode },
                                });

                                if (runMode && traceId) {
                                    // P0 cognition exposure: insert plan preview for code-affecting intents
                                    // Pass message content for client-side metabuild gate (no backend dependency)
                                    if (isCodeAffectingIntent(intentClass, intentName, content)) {
                                        const studioContext = await getStudioRepoContext();
                                        const plan: PlanPreviewData = {
                                            status: 'pending',
                                            prompt: content,
                                            traceId,
                                            intentClass,
                                            intentName,
                                            runMode: true,
                                            steps: buildPlanSteps(intentName),
                                            context: get().currentContext,
                                            studioContext,
                                            createdAt: new Date().toISOString(),
                                        };
                                        get().addMessage({
                                            sender: 'system',
                                            content: JSON.stringify(plan),
                                            type: 'plan-preview',
                                            messageType: 'chat',
                                            buildCredits: 0,
                                            metadata: {
                                                traceId,
                                                intentClass,
                                                intentName,
                                                runMode,
                                                planPreview: plan,
                                            },
                                        });
                                        set({ isLoading: false });
                                        return;
                                    }

                                    await executeTracedSomaRun({
                                        content,
                                        intentClass,
                                        intentName,
                                        runMode,
                                        decisionTraceId: traceId,
                                    });
                                    return;
                                }
                                // If not runMode, fall through to REST chat but decision is already shown
                            }
                        } catch (classifyError) {
                            // Show classify failure for visibility
                            console.warn('[ChatStore] Classify failed:', classifyError);
                            get().addMessage({
                                sender: 'system',
                                content: `Decision visibility unavailable: classify failed`,
                                type: 'assistant-log',
                                messageType: 'chat',
                                buildCredits: 0,
                            });
                            // Fall through to regular chat
                        }
                    }

                    if (PREFER_REST_CHAT) {
                        // Add user message immediately (optimistic update) so user sees their message right away
                        // instead of just "Processing..." while waiting for the API response
                        // Skip if we already added a user message from the classify path
                        let optimisticMessageId: string | null = null;
                        if (!userMessageAdded) {
                            optimisticMessageId = `pending_${generateMessageId()}`;
                            const optimisticUserMessage: ChatMessage = {
                                id: optimisticMessageId,
                                sender: 'user',
                                content,
                                type: 'text',
                                timestamp: new Date().toISOString(),
                                buildCredits: Math.ceil(content.length / 4),
                            };
                            get().addMessage(optimisticUserMessage);
                        }

                        try {
                            let currentSessionId = get().sessionId;

                            if (!currentSessionId) {
                                const missionId = get().missionId;

                                if (missionId) {
                                    try {
                                        const sessionsResponse =
                                            await restChatAPI.getChatSessionByMission(missionId);
                                        if (
                                            sessionsResponse.success &&
                                            sessionsResponse.sessions.length > 0
                                        ) {
                                            currentSessionId = sessionsResponse.sessions[0].id;
                                            set({ sessionId: currentSessionId });
                                            await get().hydrateFromBackend(currentSessionId);
                                        }
                                    } catch (error) {
                                        console.warn(
                                            '[ChatStore] Failed to find session by mission:',
                                            error
                                        );
                                    }
                                }

                                if (!currentSessionId) {
                                    const createResponse = await restChatAPI.createChatSession({
                                        missionId: get().missionId ?? undefined,
                                        title: 'New Session',
                                    });
                                    currentSessionId = createResponse.sessionId;
                                    set({
                                        sessionId: currentSessionId,
                                        sessionTitle: createResponse.title ?? null,
                                    });
                                }
                            }

                            // Use true token-by-token streaming for ChatGPT-like experience
                            // Tokens are displayed as they arrive from the LLM
                            let streamingMessageId: string | null = null;
                            let accumulatedContent = '';

                            for await (const segment of restChatAPI.streamChatMessage(
                                currentSessionId,
                                content
                            )) {
                                // Handle individual tokens for smooth progressive rendering
                                if (segment.type === 'token' && segment.token) {
                                    accumulatedContent += segment.token;

                                    if (!streamingMessageId) {
                                        // Create a new streaming message on first token
                                        streamingMessageId = `streaming_${generateMessageId()}`;
                                        const streamingMessage: ChatMessage = {
                                            id: streamingMessageId,
                                            sender: 'assistant',
                                            content: accumulatedContent,
                                            type: 'text',
                                            messageType: 'chat',
                                            timestamp: new Date().toISOString(),
                                            buildCredits: 0,
                                        };
                                        set((state: ChatStore) => ({
                                            messages: [...state.messages, streamingMessage],
                                        }));
                                    } else {
                                        // Update existing message with accumulated content
                                        set((state: ChatStore) => ({
                                            messages: state.messages.map(m =>
                                                m.id === streamingMessageId
                                                    ? { ...m, content: accumulatedContent }
                                                    : m
                                            ),
                                        }));
                                    }
                                } else if (segment.type === 'done') {
                                    // Streaming complete - finalize the message
                                    if (streamingMessageId && segment.assistantMessage) {
                                        // Replace streaming message with final persisted message
                                        // Include execution envelope for constitutional compliance
                                        // CQC-F-017: Use message type from backend (soma-response for Copy/Share rendering)
                                        const executionEnvelope = segment.execution;
                                        const backendType = segment.assistantMessage.type;
                                        const finalType: ChatMessage['type'] =
                                            backendType === 'soma-response' ||
                                            backendType === 'pluginCard' ||
                                            backendType === 'code'
                                                ? backendType
                                                : 'soma-response'; // Default to soma-response for assistant messages
                                        set((state: ChatStore) => ({
                                            messages: state.messages.map(m =>
                                                m.id === streamingMessageId
                                                    ? {
                                                          ...m,
                                                          id: segment.assistantMessage!.id,
                                                          content:
                                                              segment.assistantMessage!.content ||
                                                              accumulatedContent,
                                                          type: finalType,
                                                          buildCredits: Math.ceil(
                                                              (segment.assistantMessage!.content
                                                                  ?.length ||
                                                                  accumulatedContent.length) / 4
                                                          ),
                                                          // Pass execution envelope for verdict-based rendering
                                                          // CQC-F-022: Store fullContent as contentRaw for copy operations
                                                          metadata: {
                                                              ...m.metadata,
                                                              ...segment.assistantMessage!.metadata,
                                                              executionEnvelope: executionEnvelope,
                                                              contentRaw:
                                                                  segment.fullContent || undefined,
                                                          },
                                                      }
                                                    : m
                                            ),
                                        }));
                                    } else if (!streamingMessageId && segment.assistantMessage) {
                                        // Fail-open: stream sent done with response but no tokens
                                        // Create the assistant message directly
                                        // CQC-F-017: Use message type from backend (soma-response for Copy/Share rendering)
                                        const executionEnvelope = segment.execution;
                                        const backendType = segment.assistantMessage.type;
                                        const messageType: ChatMessage['type'] =
                                            backendType === 'soma-response' ||
                                            backendType === 'pluginCard' ||
                                            backendType === 'code'
                                                ? backendType
                                                : 'soma-response'; // Default to soma-response for assistant messages
                                        // CQC-F-022: Store fullContent as contentRaw for copy operations
                                        const newMessage: ChatMessage = {
                                            id: segment.assistantMessage.id || generateMessageId(),
                                            sender: 'assistant',
                                            content: segment.assistantMessage.content || '',
                                            type: messageType,
                                            messageType: 'chat',
                                            timestamp: new Date().toISOString(),
                                            buildCredits: Math.ceil(
                                                (segment.assistantMessage.content?.length || 0) / 4
                                            ),
                                            metadata: {
                                                ...segment.assistantMessage.metadata,
                                                executionEnvelope: executionEnvelope,
                                                contentRaw: segment.fullContent || undefined,
                                            },
                                        };
                                        set((state: ChatStore) => ({
                                            messages: [...state.messages, newMessage],
                                        }));
                                    }
                                    streamingMessageId = null;
                                    accumulatedContent = '';
                                } else if (segment.type === 'error') {
                                    // Handle streaming error
                                    console.error('[ChatStore] Streaming error:', segment.error);
                                    if (streamingMessageId) {
                                        // Update streaming message to show error
                                        set((state: ChatStore) => ({
                                            messages: state.messages.map(m =>
                                                m.id === streamingMessageId
                                                    ? {
                                                          ...m,
                                                          content:
                                                              accumulatedContent ||
                                                              `Error: ${segment.error}`,
                                                      }
                                                    : m
                                            ),
                                        }));
                                    } else {
                                        // Fail-open: stream errored before any tokens
                                        // Create an error message so user sees feedback
                                        get().addMessage({
                                            sender: 'assistant',
                                            content: `Error: ${segment.error || 'Stream failed'}`,
                                            type: 'text',
                                            messageType: 'chat',
                                            buildCredits: 0,
                                        });
                                    }
                                } else if (segment.type === 'text') {
                                    // Legacy text segment (backward compatibility)
                                    get().addMessage({
                                        sender: 'assistant',
                                        content: segment.text || '',
                                        type: 'text',
                                        messageType: 'chat',
                                        buildCredits: Math.ceil((segment.text?.length || 0) / 4),
                                    });
                                } else if (
                                    segment.type === 'report' ||
                                    segment.type === 'task' ||
                                    segment.type === 'approval' ||
                                    segment.type === 'notice'
                                ) {
                                    // Operational segments (reports, tasks, etc.)
                                    get().addMessage({
                                        sender: 'assistant',
                                        content: JSON.stringify({
                                            type: segment.type,
                                            text: segment.text,
                                            data:
                                                segment.report ||
                                                segment.task ||
                                                segment.approval ||
                                                segment.notice,
                                            actions: segment.actions || [],
                                            artifacts: segment.artifacts || [],
                                            lineage: segment.lineage || [],
                                        }),
                                        type: 'pluginCard',
                                        messageType: 'chat',
                                        buildCredits: 5,
                                    });
                                }
                            }

                            // Phase D: Trigger auto-naming after message is sent
                            // This will only name the session once after 2 user messages
                            get().maybeAutoNameSession();
                        } catch (error) {
                            console.error('[ChatStore] REST chat error:', error);
                            // Remove the optimistic message on error
                            set({
                                messages: get().messages.filter(m => m.id !== optimisticMessageId),
                            });
                            get().addMessage({
                                sender: 'system',
                                content:
                                    error instanceof Error
                                        ? `Error: ${error.message}`
                                        : 'Sorry, I encountered an error processing your request. Please try again.',
                                type: 'text',
                                messageType: 'chat',
                                buildCredits: 0,
                            });
                        }
                    } else {
                        const userMessage: ChatMessage = {
                            id: generateMessageId(),
                            sender: 'user',
                            content,
                            type: 'text',
                            timestamp: new Date().toISOString(),
                            buildCredits: Math.ceil(content.length / 4),
                        };
                        get().addMessage(userMessage);

                        try {
                            const sessionId = get().sessionId || 'unknown';

                            for await (const segment of chatAPI.streamChatResponse(
                                content,
                                sessionId
                            )) {
                                if (segment.type === 'text') {
                                    get().addMessage({
                                        sender: 'assistant',
                                        content: segment.text || '',
                                        type: 'text',
                                        messageType: 'chat',
                                        buildCredits: Math.ceil((segment.text?.length || 0) / 4),
                                        ...(segment.meta ? { metadata: segment.meta } : {}),
                                    });
                                } else if (
                                    segment.type === 'report' ||
                                    segment.type === 'task' ||
                                    segment.type === 'approval' ||
                                    segment.type === 'notice'
                                ) {
                                    get().addMessage({
                                        sender: 'assistant',
                                        content: JSON.stringify({
                                            type: segment.type,
                                            text: segment.text,
                                            data:
                                                segment.report ||
                                                segment.task ||
                                                segment.approval ||
                                                segment.notice,
                                            actions: segment.actions || [],
                                            artifacts: segment.artifacts || [],
                                            lineage: segment.lineage || [],
                                        }),
                                        type: 'pluginCard',
                                        messageType: 'chat',
                                        buildCredits: 5,
                                    });
                                }
                            }
                        } catch (error) {
                            console.error('Chat API error:', error);
                            get().addMessage({
                                sender: 'system',
                                content:
                                    error instanceof Error
                                        ? `Error: ${error.message}`
                                        : 'Sorry, I encountered an error processing your request. Please try again.',
                                type: 'text',
                                messageType: 'chat',
                                buildCredits: 0,
                            });
                        }
                    }

                    set({ isLoading: false });
                },

                setSessionId: (id: string) => {
                    set({ sessionId: id });
                },

                setSessionTitle: (title: string) => {
                    const truncatedTitle =
                        title.length > 30 ? title.substring(0, 30).trim() : title;
                    set({ sessionTitle: truncatedTitle });
                },

                setMissionId: (missionId: string | null) => {
                    set({ missionId });

                    if (PREFER_REST_CHAT && missionId) {
                        restChatAPI
                            .getChatSessionByMission(missionId)
                            .then(response => {
                                if (response.success && response.sessions.length > 0) {
                                    const session = response.sessions[0];
                                    set({ sessionId: session.id });
                                    get().hydrateFromBackend(session.id);
                                }
                            })
                            .catch(error => {
                                console.warn(
                                    '[ChatStore] Failed to find session by mission on setMissionId:',
                                    error
                                );
                            });
                    }
                },

                hydrateFromBackend: async (sessionId: string) => {
                    if (!PREFER_REST_CHAT) {
                        return;
                    }

                    const hydratedSessions = get().hydratedSessions;
                    if (hydratedSessions.has(sessionId)) {
                        return;
                    }

                    try {
                        const response = await restChatAPI.getChatSession(sessionId);
                        if (response.success) {
                            const mappedMessages = mapBackendHistoryToUi(response.messages);
                            set({
                                messages: mappedMessages,
                                sessionTitle: response.session.title ?? get().sessionTitle,
                                hydratedSessions: new Set([...hydratedSessions, sessionId]),
                            });

                            if (typeof window !== 'undefined' && sessionId) {
                                try {
                                    localStorage.removeItem(`beehive-session-${sessionId}`);
                                } catch (error) {
                                    console.warn(
                                        '[ChatStore] Failed to clear localStorage for session:',
                                        error
                                    );
                                }
                            }
                        }
                    } catch (error) {
                        console.error('[ChatStore] Failed to hydrate from backend:', error);
                    }
                },

                addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
                    const newMessage: ChatMessage = {
                        ...message,
                        id: generateMessageId(),
                        timestamp: new Date().toISOString(),
                    };

                    if (newMessage.sender === 'bee' && typeof window !== 'undefined') {
                        window.dispatchEvent(
                            new CustomEvent('beehive:bee-output', { detail: newMessage })
                        );
                    }

                    set((state: ChatStore) => ({
                        messages: [...state.messages, newMessage],
                    }));
                },

                addChat: (msg: { author: 'user' | 'assistant'; text: string }) => {
                    const newMessage: ChatMessage = {
                        id: generateMessageId(),
                        sender: msg.author === 'user' ? 'user' : 'bee',
                        content: msg.text,
                        type: 'text',
                        messageType: 'chat',
                        timestamp: new Date().toISOString(),
                    };

                    if (newMessage.sender === 'bee' && typeof window !== 'undefined') {
                        window.dispatchEvent(
                            new CustomEvent('beehive:bee-output', { detail: newMessage })
                        );
                    }

                    set((state: ChatStore) => ({
                        messages: [...state.messages, newMessage],
                    }));
                },

                addFeedback: (text: string) => {
                    const newMessage: ChatMessage = {
                        id: generateMessageId(),
                        sender: 'system',
                        content: text,
                        type: 'text',
                        messageType: 'feedback',
                        timestamp: new Date().toISOString(),
                        buildCredits: 0,
                    };

                    set((state: ChatStore) => ({
                        messages: [...state.messages, newMessage],
                    }));
                },

                switchControlMode: (mode: BeeControlMode) => {
                    set({ controlMode: mode, beeControlMode: mode });

                    get().addMessage({
                        sender: 'system',
                        content: `Control mode switched to ${mode}. ${
                            mode === 'manual'
                                ? 'All actions require explicit approval.'
                                : mode === 'semi'
                                  ? 'Safe operations will execute automatically.'
                                  : 'Maximum AI control enabled with emergency override.'
                        }`,
                        type: 'text',
                        messageType: 'chat',
                        buildCredits: 15,
                    });
                },

                switchMode: (mode: BeeControlMode) => {
                    get().switchControlMode(mode);
                },

                setControlMode: (mode: BeeControlMode) => {
                    set({ controlMode: mode, beeControlMode: mode });
                },

                setLoading: (loading: boolean) => {
                    set({ isLoading: loading });
                },

                clearMessages: () => {
                    set({ messages: [] });
                },

                injectWebhookResult: (message: ChatMessage) => {
                    set((state: ChatStore) => ({
                        messages: [...state.messages, message],
                    }));
                },

                executeCommand: async (command: string) => {
                    const result = await commandRouter.execute(command);
                    if (result) {
                        if (result.data?.['triggerSummary']) {
                            const summaryMessage = await sessionSummaryService.endSession(
                                get().sessionId || 'unknown',
                                get().messages
                            );
                            get().addMessage(summaryMessage);
                        } else {
                            get().addMessage({
                                sender: 'system',
                                content: result.message,
                                type: result.success ? 'text' : 'text',
                                messageType: 'chat',
                                buildCredits: Math.ceil(result.message.length / 4),
                                ...(result.data && {
                                    relatedTaskId: (result.data as Record<string, unknown>)[
                                        'taskId'
                                    ] as string,
                                }),
                            });
                        }
                    }
                },

                getMessagesByTaskId: (taskId: string) => {
                    return get().messages.filter(msg => msg.relatedTaskId === taskId);
                },

                simulateWebhookEvents: async () => {
                    await arcWebhookHandler.injectWebhookResults(get().addMessage);
                },

                simulateCIEvents: async () => {
                    arcTelemetryService.simulateCIEvents(message => {
                        get().addMessage({
                            sender: message.sender,
                            content: message.content,
                            type: message.type,
                            messageType: 'chat',
                            buildCredits: message.buildCredits,
                            relatedTaskId: message.relatedTaskId,
                        });
                    });
                },

                initializeWebhookListeners: () => {
                    const handleWebhook = async (event: Event) => {
                        const customEvent = event as CustomEvent;
                        const { type, data } = customEvent.detail;
                        try {
                            const result = await arcWebhookHandler.simulateWebhookEvent(type, data);
                            if (result.shouldInject) {
                                get().addMessage({
                                    sender: result.message.sender,
                                    content: result.message.content,
                                    type: result.message.type,
                                    messageType: 'chat',
                                    buildCredits: result.message.buildCredits,
                                    relatedTaskId: result.message.relatedTaskId,
                                });

                                if (
                                    type === 'task_completed' ||
                                    type === 'task_failed' ||
                                    type === 'requires_input'
                                ) {
                                    try {
                                        const bell = new Audio('/sounds/bell.mp3');
                                        bell.play().catch(() => {
                                            /* ignore audio errors */
                                        });
                                    } catch (error) {
                                        console.warn('Could not play bell sound:', error);
                                    }
                                }

                                if (notifyFunction) {
                                    const { notifications } = useSettingsStore.getState();

                                    if (type === 'task_completed' && notifications.desktop) {
                                        notifyFunction('Task Complete', {
                                            body: `"${data.summary || 'Task'}" finished successfully.`,
                                            icon: '/favicon.ico',
                                            tag: 'task_completed',
                                        });
                                    } else if (type === 'task_failed' && notifications.desktop) {
                                        notifyFunction('Task Failed', {
                                            body: `"${data.summary || 'Task'}" encountered an error.`,
                                            icon: '/favicon.ico',
                                            tag: 'task_failed',
                                        });
                                    } else if (type === 'requires_input' && notifications.desktop) {
                                        notifyFunction('Input Required', {
                                            body: `Beehive needs your input: ${data.prompt || 'Please check the workspace'}`,
                                            icon: '/favicon.ico',
                                            tag: 'requires_input',
                                        });
                                    }
                                }
                            }
                        } catch (error) {
                            console.error('Webhook simulation error:', error);
                        }
                    };

                    const handleCI = async (event: Event) => {
                        const customEvent = event as CustomEvent;
                        const { data } = customEvent.detail;
                        try {
                            const message = await arcTelemetryService.processCIStatusEvent({
                                id: `ci_${Date.now()}`,
                                type: data.status === 'success' ? 'ci_success' : 'ci_failure',
                                repository: 'DDPMLS/beehive-workspace',
                                branch: 'devin/1750215936-beehive-migration-fresh',
                                commit: Math.random().toString(36).substr(2, 7),
                                workflow: data.workflow,
                                duration: data.duration,
                                timestamp: new Date(),
                            });
                            get().addMessage({
                                sender: message.sender,
                                content: message.content,
                                type: message.type,
                                messageType: 'chat',
                                buildCredits: message.buildCredits,
                                relatedTaskId: message.relatedTaskId,
                            });
                        } catch (error) {
                            console.error('CI status processing error:', error);
                        }
                    };

                    if (typeof window !== 'undefined') {
                        window.addEventListener('beehive:webhook', handleWebhook);
                        window.addEventListener('beehive:ci', handleCI);
                    }
                },

                updateTaskTitle: (title: string) => {
                    const currentMetadata = get().taskMetadata;
                    if (currentMetadata) {
                        set({
                            taskMetadata: {
                                ...currentMetadata,
                                title,
                            },
                        });
                    }
                },

                generateTaskTitle: async (firstMessage: string) => {
                    try {
                        const content = firstMessage.toLowerCase();
                        let title = 'Untitled Task';

                        if (
                            content.includes('fix') ||
                            content.includes('bug') ||
                            content.includes('error')
                        ) {
                            if (
                                content.includes('deploy') ||
                                content.includes('netlify') ||
                                content.includes('ci')
                            ) {
                                title = 'Fix deployment issue';
                            } else if (content.includes('test') || content.includes('testing')) {
                                title = 'Fix test issues';
                            } else {
                                title = 'Fix bug';
                            }
                        } else if (
                            content.includes('implement') ||
                            content.includes('add') ||
                            content.includes('create')
                        ) {
                            if (
                                content.includes('ui') ||
                                content.includes('interface') ||
                                content.includes('component')
                            ) {
                                title = 'Implement UI component';
                            } else if (content.includes('api') || content.includes('endpoint')) {
                                title = 'Implement API endpoint';
                            } else if (content.includes('feature')) {
                                title = 'Implement new feature';
                            } else {
                                title = 'Add new functionality';
                            }
                        } else if (
                            content.includes('update') ||
                            content.includes('modify') ||
                            content.includes('change')
                        ) {
                            title = 'Update configuration';
                        } else if (content.includes('test') || content.includes('testing')) {
                            title = 'Run tests';
                        } else if (content.includes('deploy') || content.includes('deployment')) {
                            title = 'Deploy application';
                        } else if (content.includes('refactor') || content.includes('cleanup')) {
                            title = 'Refactor code';
                        } else {
                            const words = firstMessage
                                .replace(/[^\w\s]/g, '')
                                .split(/\s+/)
                                .filter(word => word.length > 2)
                                .slice(0, 4);

                            if (words.length > 0) {
                                title = words.join(' ').substring(0, 50);
                                title = title.charAt(0).toUpperCase() + title.slice(1);
                            }
                        }

                        const existingTitles = JSON.parse(
                            localStorage.getItem('beehive-task-titles') || '[]'
                        );
                        let uniqueTitle = title;
                        let counter = 1;

                        while (existingTitles.includes(uniqueTitle)) {
                            uniqueTitle = `${title} (${counter})`;
                            counter++;
                        }

                        existingTitles.push(uniqueTitle);
                        localStorage.setItem('beehive-task-titles', JSON.stringify(existingTitles));

                        set({
                            taskMetadata: {
                                title: uniqueTitle,
                                createdAt: new Date(),
                                sessionId: get().sessionId || 'unknown',
                                taskId: '0543532',
                            },
                        });
                    } catch {
                        set({
                            taskMetadata: {
                                title: 'Untitled Task',
                                createdAt: new Date(),
                                sessionId: get().sessionId || 'unknown',
                                taskId: '0543532',
                            },
                        });
                    }
                },

                generateSessionTitle: async (firstMessage: string) => {
                    try {
                        const { handleCompletion } = await import('../api/completion');
                        const response = await handleCompletion('gpt-4', {
                            messages: [
                                {
                                    role: 'user',
                                    content: `Summarize this task in 3-5 words: "${firstMessage}"`,
                                },
                            ],
                            temperature: 0.3,
                            max_tokens: 20,
                        });

                        const summary = response.choices?.[0]?.message?.content?.trim();
                        if (
                            summary &&
                            summary !==
                                "I'm a Bee agent ready to assist with autonomous task execution. I can help with code analysis, deployment, testing, and system management. How can I help you today?"
                        ) {
                            const truncatedSummary =
                                summary.length > 30 ? summary.substring(0, 30).trim() : summary;
                            set({ sessionTitle: truncatedSummary });
                        } else {
                            const words = firstMessage.split(' ').slice(0, 4).join(' ');
                            const truncatedWords =
                                words.length > 30 ? words.substring(0, 30).trim() : words;
                            set({ sessionTitle: truncatedWords || 'New Session' });
                        }
                    } catch {
                        const words = firstMessage.split(' ').slice(0, 4).join(' ');
                        const truncatedWords =
                            words.length > 30 ? words.substring(0, 30).trim() : words;
                        set({ sessionTitle: truncatedWords || 'New Session' });
                    }
                },

                saveCurrentSession: () => {
                    const state = get();
                    if (state.sessionId) {
                        const sessionData = {
                            messages: state.messages,
                            sessionTitle: state.sessionTitle,
                            taskMetadata: state.taskMetadata,
                            controlMode: state.controlMode,
                            editorContent: state.editorContent,
                            openFiles: state.openFiles,
                            activeFile: state.activeFile,
                            savedAt: new Date().toISOString(),
                        };
                        localStorage.setItem(
                            `beehive-session-${state.sessionId}`,
                            JSON.stringify(sessionData)
                        );
                    }
                },

                setEditorContent: (content: string, fileName = 'main.py') => {
                    const state = get();
                    const sessionId = state.sessionId;
                    set({
                        editorContent: {
                            ...state.editorContent,
                            [`${sessionId}:${fileName}`]: content,
                        },
                    });
                },

                getEditorContent: (fileName = 'main.py') => {
                    const state = get();
                    const sessionId = state.sessionId;
                    return state.editorContent[`${sessionId}:${fileName}`] || '';
                },

                setOpenFiles: (files: string[]) => {
                    const state = get();
                    const sessionId = state.sessionId;
                    if (!sessionId) return;
                    set({
                        openFiles: {
                            ...state.openFiles,
                            [sessionId]: files,
                        },
                    });
                },

                getOpenFiles: () => {
                    const state = get();
                    const sessionId = state.sessionId;
                    if (!sessionId) return ['main.py'];
                    return state.openFiles[sessionId] || ['main.py'];
                },

                setActiveFile: (fileName: string) => {
                    const state = get();
                    const sessionId = state.sessionId;
                    if (!sessionId) return;
                    set({
                        activeFile: {
                            ...state.activeFile,
                            [sessionId]: fileName,
                        },
                    });
                },

                getActiveFile: () => {
                    const state = get();
                    const sessionId = state.sessionId;
                    if (!sessionId) return 'main.py';
                    return state.activeFile[sessionId] || 'main.py';
                },

                loadSession: async (sessionId: string) => {
                    try {
                        get().saveCurrentSession();

                        const sessionData = localStorage.getItem(`beehive-session-${sessionId}`);
                        if (sessionData) {
                            const parsed = JSON.parse(sessionData);
                            set({
                                sessionId,
                                messages: parsed.messages || [],
                                sessionTitle: parsed.sessionTitle || null,
                                taskMetadata: parsed.taskMetadata || null,
                                controlMode: parsed.controlMode || 'manual',
                                beeControlMode: parsed.controlMode || 'manual',
                                editorContent: parsed.editorContent || {},
                                openFiles: parsed.openFiles || {},
                                activeFile: parsed.activeFile || {},
                            });
                        } else {
                            set({
                                sessionId,
                                messages: [],
                                sessionTitle: null,
                                taskMetadata: null,
                                controlMode: 'manual',
                                beeControlMode: 'manual',
                                editorContent: {},
                                openFiles: {},
                                activeFile: {},
                            });
                        }
                    } catch (error) {
                        console.error('Failed to load session:', error);
                    }
                },

                setSession: (id: string) => {
                    const state = get();
                    if (!state.sessions.find(s => s.id === id)) return;
                    get().saveCurrentSession();
                    get().loadSession(id);
                },

                renameSession: (id: string, newName: string) => {
                    set(state => ({
                        sessions: state.sessions.map(s =>
                            s.id === id ? { ...s, name: newName } : s
                        ),
                    }));
                    if (get().sessionId === id) {
                        set({ sessionTitle: newName });
                    }
                },

                createSession: ({
                    id,
                    title,
                    plugins: _plugins,
                    messages = [],
                }: {
                    id: string;
                    title: string;
                    plugins: string[];
                    messages?: Array<{ role: string; text: string }>;
                }) => {
                    get().clearMessages();
                    set({
                        sessionId: id,
                        sessionTitle: title,
                        messages: messages.map(msg => ({
                            id: generateMessageId(),
                            sender: msg.role === 'user' ? 'user' : 'system',
                            content: msg.text,
                            type: 'text',
                            messageType: 'chat',
                            timestamp: new Date().toISOString(),
                            buildCredits: 0,
                        })),
                    });
                },

                createNewSession: () => {
                    const newSessionId = generateSessionId();
                    const newSession: Session = {
                        id: newSessionId,
                        name: 'New Session',
                        createdAt: new Date().toISOString(),
                    };

                    get().saveCurrentSession();

                    get().clearMessages();

                    set(state => ({
                        sessions: [...state.sessions, newSession],
                        sessionId: newSessionId,
                        sessionTitle: 'New Session',
                        messages: [],
                        taskMetadata: null,
                        controlMode: 'manual',
                        beeControlMode: 'manual',
                        editorContent: {},
                        openFiles: {},
                        activeFile: {},
                        prCount: 0,
                        ciCount: 0,
                        buildCredits: 0,
                    }));

                    return newSessionId;
                },

                searchTasks: (query: string) => {
                    const { sessions } = get();
                    if (!query.trim()) {
                        return sessions.slice(0, 10);
                    }

                    const lowerQuery = query.toLowerCase();
                    return sessions
                        .filter(session => {
                            const nameMatch = session.name.toLowerCase().includes(lowerQuery);

                            try {
                                const sessionData = localStorage.getItem(
                                    `beehive-session-${session.id}`
                                );
                                if (sessionData) {
                                    const parsed = JSON.parse(sessionData);
                                    const messageMatch = parsed.messages?.some(
                                        (msg: Record<string, unknown>) =>
                                            (msg['content'] as string)
                                                ?.toLowerCase()
                                                .includes(lowerQuery)
                                    );
                                    return nameMatch || messageMatch;
                                }
                            } catch (error) {
                                console.warn('Error parsing session data for search:', error);
                            }

                            return nameMatch;
                        })
                        .slice(0, 10);
                },

                addLogChunks: (log: BeeLogChunk[]) => {
                    const newMessage: ChatMessage = {
                        id: generateMessageId(),
                        sender: 'bee',
                        content: '',
                        type: 'assistant-log',
                        messageType: 'chat',
                        timestamp: new Date().toISOString(),
                        buildCredits: 0,
                        log: log,
                    };

                    set((state: ChatStore) => ({
                        messages: [...state.messages, newMessage],
                    }));
                },

                executeAgenticCommand: async (command: string) => {
                    const trimmedCommand = command.trim();

                    if (trimmedCommand.startsWith('/panel ')) {
                        const [, action, panelId, ...args] = trimmedCommand.split(' ');
                        try {
                            const { agenticService } = await import('../services/AgenticService');
                            const result = await agenticService.executeBeeCommand(
                                'panel-manager',
                                `${action} ${panelId} ${args.join(' ')}`.trim(),
                                get().currentContext
                            );

                            get().addMessage({
                                sender: 'system',
                                content: result.success
                                    ? `Panel ${action} completed`
                                    : `Panel ${action} failed: ${result.error}`,
                                type: 'text',
                                messageType: 'chat',
                                buildCredits: 5,
                            });
                        } catch (error) {
                            const errorMessage =
                                error instanceof Error ? error.message : String(error);
                            get().addMessage({
                                sender: 'system',
                                content: `Failed to execute panel command: ${errorMessage}`,
                                type: 'text',
                                messageType: 'chat',
                                buildCredits: 0,
                            });
                        }
                        return;
                    }

                    if (trimmedCommand.startsWith('/agent ')) {
                        const [, action, agentId, ...args] = trimmedCommand.split(' ');
                        try {
                            const { agenticService } = await import('../services/AgenticService');
                            const result = await agenticService.executeBeeCommand(
                                'supervisor',
                                `${action} ${agentId} ${args.join(' ')}`.trim(),
                                get().currentContext
                            );

                            get().addMessage({
                                sender: 'system',
                                content: result.success
                                    ? `Agent ${action} completed`
                                    : `Agent ${action} failed: ${result.error}`,
                                type: 'text',
                                messageType: 'chat',
                                buildCredits: 5,
                            });
                        } catch (error) {
                            const errorMessage =
                                error instanceof Error ? error.message : String(error);
                            get().addMessage({
                                sender: 'system',
                                content: `Failed to execute agent command: ${errorMessage}`,
                                type: 'text',
                                messageType: 'chat',
                                buildCredits: 0,
                            });
                        }
                        return;
                    }

                    await get().executeCommand(command);
                },

                setContext: (context: AgenticContext) => {
                    set({ currentContext: context });
                },

                // Phase C: Clear session state for blank load or new session
                clearSession: () => {
                    // Context Canvas v1: Reset canvas to idle when clearing session
                    // This prevents canvas state from leaking across sessions
                    closeContextCanvas();

                    set({
                        sessionId: null,
                        sessionTitle: null,
                        messages: [],
                        taskMetadata: null,
                        missionId: null,
                        messagesCursor: null,
                        hasMoreMessages: false,
                        isLoadingOlder: false,
                        // Phase D: Reset auto-naming state for new session
                        sessionAutoNamed: false,
                        autoNameAttemptedAt: null,
                        autoNameAttemptCount: 0,
                        autoNameFailedUserMsgCount: null,
                        autoNameInFlight: false,
                    });
                    console.log('[ChatStore] Session cleared for blank load');
                },

                // Phase C: Load session with pagination (50 messages)
                loadSessionWithPagination: async (sessionId: string) => {
                    try {
                        set({ isLoading: true });

                        // Context Canvas v1: Reset canvas to idle when switching sessions
                        // This prevents canvas state from leaking across sessions
                        closeContextCanvas();

                        const response = await restChatAPI.getChatSession(sessionId, { limit: 50 });

                        if (response.success) {
                            const mappedMessages = mapBackendHistoryToUi(response.messages);
                            set({
                                sessionId,
                                sessionTitle: response.session.title ?? null,
                                messages: mappedMessages,
                                missionId: response.session.missionId ?? null,
                                messagesCursor: response.nextCursor ?? null,
                                hasMoreMessages: response.hasMore ?? false,
                                hydratedSessions: new Set([...get().hydratedSessions, sessionId]),
                            });
                            console.log(
                                '[ChatStore] Loaded session with pagination:',
                                sessionId,
                                'messages:',
                                mappedMessages.length,
                                'hasMore:',
                                response.hasMore
                            );
                        }
                    } catch (error) {
                        console.error('[ChatStore] Failed to load session with pagination:', error);
                    } finally {
                        set({ isLoading: false });
                    }
                },

                // Phase C: Load older messages using cursor
                loadOlderMessages: async () => {
                    const { sessionId, messagesCursor, hasMoreMessages, isLoadingOlder } = get();

                    if (!sessionId || !messagesCursor || !hasMoreMessages || isLoadingOlder) {
                        return;
                    }

                    try {
                        set({ isLoadingOlder: true });

                        const response = await restChatAPI.getChatSession(sessionId, {
                            limit: 50,
                            cursor: messagesCursor,
                        });

                        if (response.success) {
                            const olderMessages = mapBackendHistoryToUi(response.messages);
                            // Prepend older messages to existing messages
                            set({
                                messages: [...olderMessages, ...get().messages],
                                messagesCursor: response.nextCursor ?? null,
                                hasMoreMessages: response.hasMore ?? false,
                            });
                            console.log(
                                '[ChatStore] Loaded older messages:',
                                olderMessages.length,
                                'hasMore:',
                                response.hasMore
                            );
                        }
                    } catch (error) {
                        console.error('[ChatStore] Failed to load older messages:', error);
                    } finally {
                        set({ isLoadingOlder: false });
                    }
                },

                // Phase D: Auto-name session after 2-4 user messages (name freezes after first naming)
                // Retry logic: if PATCH fails, allow one controlled retry (after 30s or next user message)
                maybeAutoNameSession: async () => {
                    const {
                        sessionId,
                        sessionAutoNamed,
                        sessionTitle,
                        messages,
                        autoNameAttemptCount,
                        autoNameAttemptedAt,
                        autoNameFailedUserMsgCount,
                        autoNameInFlight,
                    } = get();

                    // Skip if no session, already auto-named, or PATCH in flight
                    if (!sessionId || sessionAutoNamed || autoNameInFlight) {
                        return;
                    }

                    // Skip if exhausted (already retried once)
                    if (autoNameAttemptCount >= 2) {
                        return;
                    }

                    // Skip if session already has a meaningful title (not "New Session")
                    if (sessionTitle && sessionTitle !== 'New Session') {
                        set({ sessionAutoNamed: true });
                        return;
                    }

                    // Count user messages
                    const userMessages = messages.filter(m => m.sender === 'user');
                    const userMsgCount = userMessages.length;

                    // Trigger auto-naming after 2 user messages (within 2-4 range per spec)
                    if (userMsgCount < 2) {
                        return;
                    }

                    // If this is a retry (attemptCount === 1), check if we're allowed to retry
                    if (autoNameAttemptCount === 1) {
                        const timeSinceFailure = autoNameAttemptedAt
                            ? Date.now() - autoNameAttemptedAt
                            : Infinity;
                        const hasNewUserMessage =
                            autoNameFailedUserMsgCount !== null &&
                            userMsgCount > autoNameFailedUserMsgCount;

                        // Allow retry if 30+ seconds passed OR a new user message arrived
                        if (timeSinceFailure < 30000 && !hasNewUserMessage) {
                            return;
                        }
                    }

                    // Get the first user message for naming
                    const firstUserMessage = userMessages[0];
                    if (!firstUserMessage) {
                        return;
                    }

                    // Derive title from first user message (simple truncation, no AI)
                    // Clean up the message: remove special chars, take first few words
                    const cleanedContent = firstUserMessage.content
                        .replace(/[^\w\s]/g, ' ') // Replace special chars with spaces
                        .replace(/\s+/g, ' ') // Collapse multiple spaces
                        .trim();

                    // Take first 5 words or 50 chars, whichever is shorter
                    const words = cleanedContent.split(' ').slice(0, 5);
                    let title = words.join(' ');
                    if (title.length > 50) {
                        title = title.substring(0, 47) + '...';
                    }

                    // Capitalize first letter
                    title = title.charAt(0).toUpperCase() + title.slice(1);

                    // Fallback if title is empty
                    if (!title || title.length < 2) {
                        title = 'Chat Session';
                    }

                    // Mark in-flight and increment attempt count for retry path
                    const isRetry = autoNameAttemptCount === 1;
                    set({
                        autoNameInFlight: true,
                        autoNameAttemptCount: isRetry ? 2 : autoNameAttemptCount,
                    });

                    try {
                        // Update title on backend
                        const response = await restChatAPI.updateSessionTitle(sessionId, title);

                        if (response.success) {
                            set({
                                sessionTitle: response.session.title ?? title,
                                sessionAutoNamed: true,
                                autoNameInFlight: false,
                                autoNameAttemptedAt: null,
                                autoNameFailedUserMsgCount: null,
                            });
                            console.log('[ChatStore] Auto-named session:', title);
                        } else {
                            // Response not successful but no error thrown
                            throw new Error('PATCH response was not successful');
                        }
                    } catch (error) {
                        console.error('[ChatStore] Failed to auto-name session:', error);

                        if (isRetry) {
                            // Second failure - exhausted, stop trying but don't mark as "named"
                            set({
                                autoNameInFlight: false,
                                autoNameAttemptCount: 2,
                            });
                            console.log('[ChatStore] Auto-naming exhausted after retry');
                        } else {
                            // First failure - record for retry
                            set({
                                autoNameInFlight: false,
                                autoNameAttemptCount: 1,
                                autoNameAttemptedAt: Date.now(),
                                autoNameFailedUserMsgCount: userMsgCount,
                            });
                            console.log(
                                '[ChatStore] Auto-naming failed, will retry on next message or after 30s'
                            );
                        }
                    }
                },

                // Update existing run-log message by traceId instead of appending duplicate
                // This ensures traced runs produce exactly ONE visible message
                updateMessageByTraceId: (
                    traceId: string,
                    updates: {
                        status?: string;
                        answer?: string;
                        steps?: unknown[];
                        failureReason?: string;
                        verdict?: string;
                        reasonCode?: string;
                        confidence?: number;
                        metaBuild?: MetaBuildResult | null;
                    }
                ): boolean => {
                    const messages = get().messages;
                    // Find the existing run-log message by traceId
                    const msgIndex = messages.findIndex(
                        m => m.type === 'run-log' && m.metadata?.traceId === traceId
                    );

                    if (msgIndex === -1) {
                        return false;
                    }

                    // Parse existing content
                    const existingMsg = messages[msgIndex];
                    let existingData: Record<string, unknown> = {};
                    try {
                        existingData = JSON.parse(existingMsg.content);
                    } catch {
                        existingData = { traceId };
                    }

                    // Merge updates
                    const updatedData = {
                        ...existingData,
                        ...(updates.status !== undefined && { status: updates.status }),
                        ...(updates.answer !== undefined && { answer: updates.answer }),
                        ...(updates.steps !== undefined && { steps: updates.steps }),
                        ...(updates.failureReason !== undefined && {
                            failureReason: updates.failureReason,
                        }),
                        ...(updates.verdict !== undefined && { verdict: updates.verdict }),
                        ...(updates.reasonCode !== undefined && { reasonCode: updates.reasonCode }),
                        ...(updates.confidence !== undefined && { confidence: updates.confidence }),
                        ...(updates.metaBuild !== undefined && { metaBuild: updates.metaBuild }),
                    };

                    // Update the message in place
                    const updatedMsg: ChatMessage = {
                        ...existingMsg,
                        content: JSON.stringify(updatedData),
                        metadata: {
                            ...existingMsg.metadata,
                            ...(updates.status !== undefined && { status: updates.status }),
                            ...(updates.verdict !== undefined && { verdict: updates.verdict }),
                            ...(updates.reasonCode !== undefined && {
                                reasonCode: updates.reasonCode,
                            }),
                            ...(updates.confidence !== undefined && {
                                confidence: updates.confidence,
                            }),
                            ...(updates.metaBuild !== undefined && {
                                metaBuild: updates.metaBuild,
                            }),
                        },
                    };

                    set({
                        messages: [
                            ...messages.slice(0, msgIndex),
                            updatedMsg,
                            ...messages.slice(msgIndex + 1),
                        ],
                    });

                    return true;
                },

                approvePlanPreview: async (messageId: string) => {
                    const msg = get().messages.find(m => m.id === messageId);
                    if (!msg || msg.type !== 'plan-preview') return;
                    let plan: PlanPreviewData | null = null;
                    try {
                        plan = JSON.parse(msg.content) as PlanPreviewData;
                    } catch {
                        plan = null;
                    }
                    if (!plan || plan.status !== 'pending') return;

                    updateMessageById(messageId, m => ({
                        ...m,
                        content: JSON.stringify({ ...plan, status: 'approved' }),
                        metadata: { ...m.metadata, planPreview: { ...plan, status: 'approved' } },
                    }));

                    set({ isLoading: true });
                    await executeTracedSomaRun({
                        content: plan.prompt,
                        intentClass: plan.intentClass,
                        intentName: plan.intentName,
                        runMode: plan.runMode,
                        decisionTraceId: plan.traceId,
                    });
                },

                cancelPlanPreview: (messageId: string) => {
                    const msg = get().messages.find(m => m.id === messageId);
                    if (!msg || msg.type !== 'plan-preview') return;
                    let plan: PlanPreviewData | null = null;
                    try {
                        plan = JSON.parse(msg.content) as PlanPreviewData;
                    } catch {
                        plan = null;
                    }
                    if (!plan || plan.status !== 'pending') return;

                    updateMessageById(messageId, m => ({
                        ...m,
                        content: JSON.stringify({ ...plan, status: 'cancelled' }),
                        metadata: { ...m.metadata, planPreview: { ...plan, status: 'cancelled' } },
                    }));
                    get().addMessage({
                        sender: 'system',
                        content: 'Cancelled. No execution was started.',
                        type: 'text',
                        messageType: 'chat',
                        buildCredits: 0,
                    });
                    set({ isLoading: false });
                },
            };
        },
        {
            name: 'beehive-chat-v3',
            partialize: state => ({
                messages: state.messages,
                sessionId: state.sessionId,
                sessionTitle: state.sessionTitle,
                taskMetadata: state.taskMetadata,
                controlMode: state.controlMode,
                beeControlMode: state.beeControlMode,
                prCount: state.prCount,
                ciCount: state.ciCount,
                buildCredits: state.buildCredits,
            }),
        }
    )
);
